import templates from '../../templates'
import components from '../../components'
import dashboardComponents from '../components'

import {
  WIDTH as WIDTHPdf,
  HEIGHT as HEIGHTPdf,
  GAP,
  PADDING,
} from '../../constants'
import constants from 'constants/index'

import { calculateAspectRatioFit, convertMultipleToCanvas } from '../../helpers'

import palette from 'theme/palette'

const suppliers = async ({
  pdf,
  date,
  supplierStats,
  customStyles,
  skippedPages,
}) => {
  const WIDTH = WIDTHPdf(pdf)
  const HEIGHT = HEIGHTPdf(pdf)

  const { suppliers: suppliersIds } = constants.myLgpd.GRAPH_IDS

  const canvases = await convertMultipleToCanvas([
    suppliersIds.mySupplierCompliance,
    suppliersIds.questionnaireProgress,
    suppliersIds.highCompliancers,
    suppliersIds.lowCompliancers,
    suppliersIds.preAvaliatedHighCompliancers,
    suppliersIds.preAvaliatedLowCompliancers,
  ])

  templates.chapterCover({
    pdf,
    title: 'FORNECEDORES',
    subtitle: 'Síntese das principais informações de seus fornecedores',
    customStyles,
  })

  pdf.addPage('a4', 'p')

  components.lineWithDate(pdf, date)

  components.numberAndTitle(pdf, '6', 'Fornecedores')

  components.paragraph(
    pdf,
    'De acordo com a LGPD, fornecedores são empresas ou entidades terceirizadas que prestam serviços ou fornecem produtos para uma organização. Esses fornecedores podem ter acesso a dados pessoais durante o cumprimento de suas atividades contratadas, seja como processadores de dados ou controladores conjuntos, dependendo do acordo estabelecido.\n\nA LGPD estabelece que as organizações devem garantir que seus fornecedores também estejam em conformidade com a legislação de proteção de dados, adotando medidas contratuais e técnicas para garantir a segurança e a privacidade dos dados pessoais compartilhados.\n\nAs organizações são responsáveis por selecionar fornecedores confiáveis e estabelecer acordos adequados para proteger os direitos dos titulares dos dados e garantir o cumprimento da LGPD em todas as etapas do processamento de dados.',
    GAP * 3.25,
    palette.subscription.primary.main,
  )

  // Gráficos
  const mySupplierCompliance = canvases[suppliersIds.mySupplierCompliance]

  const mySupplierComplianceProportions = calculateAspectRatioFit(
    mySupplierCompliance.width,
    mySupplierCompliance.height,
    WIDTH - PADDING * 2,
    165,
  )

  pdf.addImage(
    mySupplierCompliance.toDataURL('image/png'),
    'PNG',
    (WIDTH - PADDING * 2) * 0.4,
    GAP * 5.5,
    mySupplierComplianceProportions.width,
    mySupplierComplianceProportions.height,
  )

  const questionnaireProgress = canvases[suppliersIds.questionnaireProgress]

  const questionnaireProgressProportions = calculateAspectRatioFit(
    questionnaireProgress.width,
    questionnaireProgress.height,
    WIDTH - PADDING * 2,
    165,
  )

  pdf.addImage(
    questionnaireProgress.toDataURL('image/png'),
    'PNG',
    PADDING,
    GAP * 8.35,
    questionnaireProgressProportions.width,
    questionnaireProgressProportions.height,
  )

  components.numberPage(pdf, palette.primary.main, false, skippedPages)

  pdf.addPage('a4', 'p')

  components.lineWithDate(pdf, date)

  dashboardComponents.counter({
    pdf,
    title: 'MEUS FORNECEDORES',
    value: supplierStats?.suppliers?.length ?? 0,
    startY: GAP * 0.75,
  })

  dashboardComponents.counter({
    pdf,
    title: 'SOU FORNECEDOR',
    value: supplierStats?.providingCount ?? 0,
    startY: GAP * 0.75,
    rightSided: true,
  })

  const highCompliancers = canvases[suppliersIds.highCompliancers]

  const highCompliancersProportions = calculateAspectRatioFit(
    highCompliancers.width,
    highCompliancers.height,
    (WIDTH - PADDING * 2) / 2,
    165,
  )

  pdf.addImage(
    highCompliancers.toDataURL('image/png'),
    'PNG',
    PADDING * 0.9,
    GAP * 2,
    highCompliancersProportions.width,
    highCompliancersProportions.height,
  )

  const lowCompliancers = canvases[suppliersIds.lowCompliancers]

  const lowCompliancersProportions = calculateAspectRatioFit(
    lowCompliancers.width,
    lowCompliancers.height,
    (WIDTH - PADDING * 2) / 2,
    165,
  )

  pdf.addImage(
    lowCompliancers.toDataURL('image/png'),
    'PNG',
    (WIDTH - PADDING * 2) / 2 + GAP,
    GAP * 2,
    lowCompliancersProportions.width,
    lowCompliancersProportions.height,
  )

  components.subtitle(pdf, 'Pré-Avaliados', GAP * 4.25)

  components.paragraph(
    pdf,
    'Pré-avaliar os fornecedores é uma etapa fundamental para antecipar riscos e evitar contratações inseguras. Esse processo permite verificar se o fornecedor está em conformidade com requisitos legais, técnicos e de proteção de dados, antes mesmo do início da parceria. Assim, a empresa garante mais segurança nas decisões, reduz custos com correções futuras e fortalece a governança e a confiança nas relações comerciais.\n\nAbaixo, os gráficos demonstram quais pré-avaliados apresentam boa ou baixa conformidade, auxiliando na análise e priorização dos fornecedores mais adequados para o negócio.',
    GAP * 4.5,
    palette.subscription.primary.main,
  )

  dashboardComponents.counter({
    pdf,
    title: 'MEUS PRÉ AVALIADOS',
    value: supplierStats?.preAvaliatedSuppliers?.length ?? 0,
    startY: GAP * 6.25,
  })

  dashboardComponents.counter({
    pdf,
    title: 'SOU PRÉ AVALIADO',
    value: supplierStats?.preAvaliatedProvidingCount ?? 0,
    startY: GAP * 6.25,
    rightSided: true,
  })

  const preAvaliatedHighCompliancers =
    canvases[suppliersIds.preAvaliatedHighCompliancers]

  const preAvaliatedHighCompliancersProportions = calculateAspectRatioFit(
    preAvaliatedHighCompliancers.width,
    preAvaliatedHighCompliancers.height,
    (WIDTH - PADDING * 2) / 2,
    165,
  )

  pdf.addImage(
    preAvaliatedHighCompliancers.toDataURL('image/png'),
    'PNG',
    PADDING * 0.9,
    GAP * 7.5,
    preAvaliatedHighCompliancersProportions.width,
    preAvaliatedHighCompliancersProportions.height,
  )

  const preAvaliatedLowCompliancers =
    canvases[suppliersIds.preAvaliatedLowCompliancers]

  const preAvaliatedLowCompliancersProportions = calculateAspectRatioFit(
    preAvaliatedLowCompliancers.width,
    preAvaliatedLowCompliancers.height,
    (WIDTH - PADDING * 2) / 2,
    165,
  )

  pdf.addImage(
    preAvaliatedLowCompliancers.toDataURL('image/png'),
    'PNG',
    (WIDTH - PADDING * 2) / 2 + GAP,
    GAP * 7.5,
    preAvaliatedLowCompliancersProportions.width,
    preAvaliatedLowCompliancersProportions.height,
  )

  components.numberPage(pdf, palette.primary.main, false, skippedPages)

  pdf.addPage('a4', 'p')

  pdf.setFillColor(palette.primary.main)
  pdf.rect(0, 0, WIDTH, HEIGHT, 'F')

  components.lineWithDate(pdf, date, palette.white)

  components.subtitle(
    pdf,
    'Conformidade de meus fornecedores',
    GAP,
    palette.white,
  )

  components.paragraph(
    pdf,
    'A imagem e tabela oferecem uma visão clara da distribuição dos fornecedores em relação à conformidade com a LGPD, permitindo uma rápida análise dos fornecedores que estão com um alto nível de conformidade (acima de 80%), aqueles que estão em um nível moderado de conformidade (entre 60% e 80%), bem como os fornecedores que exigem maior atenção e medidas de melhoria para atingir a conformidade adequada.\n\nEssa tabela auxilia na avaliação e gestão dos fornecedores, promovendo a conformidade com a LGPD e o fortalecimento da proteção de dados pessoais em todas as interações com terceiros.',
    GAP * 1.25,
    palette.white,
  )

  let under20 = 0
  let under40 = 0
  let under60 = 0
  let under80 = 0
  let over80 = 0

  // eslint-disable-next-line
  supplierStats.suppliers.map((supplier) => {
    supplier.compliance <= 20 && under20++
    supplier.compliance > 20 && supplier.compliance <= 40 && under40++
    supplier.compliance > 40 && supplier.compliance <= 60 && under60++
    supplier.compliance > 60 && supplier.compliance <= 80 && under80++
    supplier.compliance > 80 && over80++
  })

  dashboardComponents.complianceHexagons(
    pdf,
    under20.toString(),
    under40.toString(),
    under60.toString(),
    under80.toString(),
    over80.toString(),
  )

  components.numberPage(pdf, palette.white, false, skippedPages)

  // Adicionar seção de Pré-Avaliados
}

export default suppliers
